/**
 * Detects a browser fetch failure that originates from the user's side — page
 * unload during navigation, tab suspension, lost connectivity, or an explicit
 * `AbortController.abort()` — rather than a real server response.
 *
 * These rejections surface as `TypeError`s with no `statusCode`. Reporting them
 * to Sentry generates large clusters of duplicate events whenever Apollo has
 * multiple in-flight queries and the page navigates away (e.g. the
 * session-expiry redirect aborts five concurrent queries at once). Callers in
 * Apollo `errorLink`s should short-circuit on these.
 *
 * Pair with a `statusCode === undefined` check before dropping — a real 4xx /
 * 5xx response carries `statusCode` and must still be reported.
 */
export function isAbortedOrOfflineFetch(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }

  if (error.name === 'AbortError') {
    return true;
  }

  return ABORTED_OR_OFFLINE_MESSAGES.has(error.message);
}

const ABORTED_OR_OFFLINE_MESSAGES = new Set([
  'Failed to fetch', // Chrome / Firefox
  'Load failed', // Safari / WebKit (desktop + iOS)
  'NetworkError when attempting to fetch resource.', // Older WebKit
  'The Internet connection appears to be offline.', // Edge / older browsers
  'Network request failed',
]);
